/**
 * Base de conhecimento da Orya.
 * Cada arquivo entra na fila só depois de marca e modelo do cadastro.
 * Visual alinhado ao mock em tmp/bc.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Funnel } from 'lucide-react';
import { IconPrinter, IconTrash } from '@tabler/icons-react';
import PermColumnFilterPopover from '../../users-customers/components/PermColumnFilterPopover';
import { BoardClickUpFilterChipDropdown } from '../../board/toolbar/BoardClickUpFilterChipDropdown';
import '../../estoque/components/KitCreateKitModal.css';
import '../../board/components/BoardEncaminharChamadoFlow.css';
import {
  deleteKnowledgeDocument,
  discardKnowledgeJob,
  fetchKnowledgeLibrary,
  retryKnowledgeJob,
  uploadKnowledgeManual,
} from './compatEquipApi';
import './CompatKnowledgeModal.css';

const ACCEPT = '.pdf,.docx,.pptx,.png,.jpg,.jpeg,.webp,.md,.csv,.xlsx';
const MAX_BYTES = 200 * 1024 * 1024;
const MODEL_SKIP = new Set(
  'impressora impressoras multifuncional mult multi laser mono color colorida jato tinta termica pb usb dpi ppm a3 a4 a4pm duplex dupl estado usada usado usad plus nova novo preta branco impr equipamento copiadora scanner plotter matricial toner sensor no de da do das dos'.split(' ')
);

function foldModel(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function modelCode(marca, nome) {
  const brand = foldModel(marca).replace(/\s+/g, '');
  const name = String(nome || '').trim();
  const folded = foldModel(name).replace(/\s+/g, '');
  const tokens = name.split(/[^A-Za-z0-9./-]+/).filter(Boolean);
  const candidates = [];
  tokens.forEach((token) => {
    const key = foldModel(token).replace(/[^a-z0-9./-]/g, '');
    if (!key || key === brand || MODEL_SKIP.has(key)) return;
    if (/^\d{2,3}v$/.test(key) || /ppm|dpi/.test(key)) return;
    if (!/\d/.test(key)) return;
    if (!/[a-z]/.test(key) && key.length < 4) return;
    if (!folded.includes(key)) return;
    candidates.push(key);
  });
  if (!candidates.length) return folded.slice(0, 120);
  candidates.sort((a, b) => b.length - a.length);
  return candidates[0].slice(0, 120);
}

function linkCode(item) {
  const resumido = String(item?.resumido || '').trim().slice(0, 120);
  if (resumido) return resumido;
  return modelCode(item?.marca, item?.modelo);
}

function draftCodes(draft) {
  return (draft?.links || []).map((link) => link.envio).filter(Boolean);
}

function jobIsActive(job) {
  return job?.status === 'na_fila' || job?.status === 'processando';
}

function manualLabel(count) {
  const n = Number(count) || 0;
  if (n === 1) return '1 manual';
  return `${n} manuais`;
}

function formatBytes(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 1048576) return `${(n / 1048576).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

function norm(value) {
  return String(value || '').trim().toLocaleLowerCase('pt-BR');
}

function isTokenChar(ch) {
  return /[\p{L}\p{N}]/u.test(ch || '');
}

function phraseInText(phrase, text) {
  const needle = norm(phrase);
  const hay = norm(text);
  if (!needle || !hay) return false;
  if (needle === hay) return true;
  if (needle.length < 4) return false;
  let from = 0;
  while (from <= hay.length - needle.length) {
    const at = hay.indexOf(needle, from);
    if (at < 0) return false;
    const beforeOk = at === 0 || !isTokenChar(hay[at - 1]);
    const end = at + needle.length;
    const afterOk = end === hay.length || !isTokenChar(hay[end]);
    if (beforeOk && afterOk) return true;
    from = at + 1;
  }
  return false;
}

function docMatchesRow(doc, row) {
  if (norm(doc.marca) !== norm(row.marca)) return false;
  const tokens = [];
  const seen = new Set();
  const push = (raw) => {
    const text = String(raw || '').trim();
    const key = norm(text);
    if (!key || seen.has(key)) return;
    seen.add(key);
    tokens.push(text);
  };
  String(doc.modelo || '').split(/[,;|\n]/).forEach(push);
  if (Array.isArray(doc.modelos)) doc.modelos.forEach(push);
  return tokens.some((token) => phraseInText(token, row.modelo));
}

function IconBook() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z" />
      <path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function IconUpload() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 15V3" />
      <path d="m7 8 5-5 5 5" />
      <path d="M20 15v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4" />
    </svg>
  );
}

function IconClose({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function IconDoc() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

export default function CompatKnowledgeModal({ open, equipments, allowedTecCodes = null, onClose, onRefreshCounts }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [sectorId, setSectorId] = useState('');
  const [nomeDoc, setNomeDoc] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('com');
  const [uploadsOpen, setUploadsOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const brandFilterRef = useRef(null);
  const [brands, setBrands] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pinnedJobs, setPinnedJobs] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [openModelKey, setOpenModelKey] = useState('');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    if (!silent) setError('');
    try {
      const data = await fetchKnowledgeLibrary();
      setPayload(data);
      setSectorId((prev) => {
        if (prev && (data.sectors || []).some((s) => String(s.id) === String(prev))) return prev;
        return data.sectors?.length === 1 ? String(data.sectors[0].id) : '';
      });
    } catch (e) {
      setError(e.message || 'Falha ao carregar a base de conhecimento');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setPinnedJobs([]);
      setConflict(null);
      setQuery('');
      setTab('com');
      setUploadsOpen(false);
      setBrandOpen(false);
      setBrands([]);
      setSelected(null);
      setDrafts([]);
      setOpenModelKey('');
      return undefined;
    }
    load();
    return undefined;
  }, [open, load]);

  const docKey = (payload?.documents || []).map((doc) => doc.docId).join(',');
  useEffect(() => {
    if (!open || !docKey) return undefined;
    onRefreshCounts?.();
    return undefined;
  }, [open, docKey, onRefreshCounts]);

  const serverJobs = payload?.jobs || [];
  const jobs = useMemo(() => {
    const serverIds = new Set(serverJobs.map((job) => job.jobId));
    return [
      ...pinnedJobs.filter((job) => !serverIds.has(job.jobId)),
      ...serverJobs,
    ].filter((job) => job.status === 'na_fila' || job.status === 'processando' || job.status === 'falhou');
  }, [pinnedJobs, serverJobs]);

  const activeJobs = jobs.some(jobIsActive);
  useEffect(() => {
    if (!open || !activeJobs) return undefined;
    const id = window.setInterval(() => load({ silent: true }), 5000);
    return () => window.clearInterval(id);
  }, [open, activeJobs, load]);

  const rows = useMemo(() => {
    const grouped = new Map();
    (equipments || []).forEach((item) => {
      if (allowedTecCodes && !allowedTecCodes.has(String(item.tecCode || ''))) return;
      const modelo = String(item.modelo || '').trim();
      if (!modelo) return;
      const marca = String(item.marca || '').trim() || '—';
      const key = `${marca}\u0001${modelo}`;
      const cur = grouped.get(key) || { marca, modelo, qtde: 0, manuais: 0 };
      cur.qtde += Number(item.qtde) || 0;
      cur.manuais = Math.max(cur.manuais, Number(item.manuais) || 0);
      grouped.set(key, cur);
    });
    return Array.from(grouped.values()).sort(
      (a, b) => b.qtde - a.qtde || a.modelo.localeCompare(b.modelo, 'pt-BR')
    );
  }, [equipments, allowedTecCodes]);

  const catalog = useMemo(() => {
    const grouped = new Map();
    (equipments || []).forEach((item) => {
      if (allowedTecCodes && !allowedTecCodes.has(String(item.tecCode || ''))) return;
      const modelo = String(item.modelo || '').trim();
      const marca = String(item.marca || '').trim();
      if (!modelo || !marca || marca === '—') return;
      const resumido = String(item.resumido || '').trim().slice(0, 120);
      const key = `${marca}\u0001${modelo}\u0001${resumido}`;
      if (!grouped.has(key)) grouped.set(key, { key, marca, modelo, resumido });
    });
    return Array.from(grouped.values()).sort(
      (a, b) => a.marca.localeCompare(b.marca, 'pt-BR') || a.modelo.localeCompare(b.modelo, 'pt-BR')
    );
  }, [equipments, allowedTecCodes]);

  const catalogByKey = useMemo(() => new Map(catalog.map((item) => [item.key, item])), [catalog]);
  const modelChoices = useMemo(() => {
    const used = new Map();
    return catalog.map((item) => {
      const sameName = catalog.some((other) => other.key !== item.key && other.marca === item.marca && other.modelo === item.modelo);
      const sameModelo = catalog.some((other) => other.key !== item.key && other.modelo === item.modelo);
      let label = item.modelo;
      if (sameName && item.resumido) label = `${item.modelo} · ${item.resumido}`;
      if (sameModelo && !label.startsWith(`${item.marca} · `)) label = `${item.marca} · ${label}`;
      const seen = used.get(label) || 0;
      used.set(label, seen + 1);
      if (seen) label = `${label} (${seen + 1})`;
      return { label, key: item.key, marca: item.marca, modelo: item.modelo };
    });
  }, [catalog]);
  const choiceByLabel = useMemo(() => new Map(modelChoices.map((choice) => [choice.label, choice])), [modelChoices]);
  const choiceByKey = useMemo(() => new Map(modelChoices.map((choice) => [choice.key, choice])), [modelChoices]);

  const maxQty = rows.reduce((max, row) => Math.max(max, row.qtde), 1);
  const withCount = rows.filter((row) => row.manuais > 0).length;
  const withoutCount = rows.length - withCount;
  const coverage = rows.length ? Math.round((withCount / rows.length) * 1000) / 10 : 0;

  const brandCounts = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => counts.set(row.marca, (counts.get(row.marca) || 0) + 1));
    return counts;
  }, [rows]);

  const brandOptions = useMemo(
    () => Array.from(brandCounts.keys()).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [brandCounts]
  );

  const visibleRows = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('pt-BR');
    return rows.filter((row) => {
      if (tab === 'com' && row.manuais <= 0) return false;
      if (tab === 'sem' && row.manuais > 0) return false;
      if (brands.length && !brands.includes(row.marca)) return false;
      if (!q) return true;
      return `${row.marca} ${row.modelo}`.toLocaleLowerCase('pt-BR').includes(q);
    });
  }, [rows, query, tab, brands]);

  const documents = payload?.documents || [];
  const sectors = payload?.sectors || [];
  const configured = !payload || payload.configured !== false;
  const linkedDocs = selected ? documents.filter((doc) => docMatchesRow(doc, selected)) : [];

  const toggleLink = (draftKey, catalogKey) => {
    const item = catalogByKey.get(catalogKey);
    if (!draftKey || !item) return;
    setDrafts((prev) => prev.map((draft) => {
      if (draft.key !== draftKey) return draft;
      const links = draft.links || [];
      const next = links.some((link) => link.catalogKey === catalogKey)
        ? links.filter((link) => link.catalogKey !== catalogKey)
        : links.concat([{ catalogKey, envio: linkCode(item) }]);
      const first = catalogByKey.get(next[0]?.catalogKey);
      return { ...draft, links: next, marca: first?.marca || '' };
    }));
  };

  const sendFile = async (file, flags = {}) => {
    if (!file) return false;
    if (file.size > MAX_BYTES) {
      setError('Arquivo excede o limite de 200MB.');
      return false;
    }
    if (!sectorId) {
      setError('Nenhum setor da Orya disponível para este envio.');
      return false;
    }
    if (!flags.marca || !flags.modelo) {
      setError('Vincule marca e modelo antes de enviar para a fila.');
      return false;
    }
    setBusy(true);
    setError('');
    try {
      const result = await uploadKnowledgeManual({
        file,
        sectorId,
        nomeDoc: flags.nomeDoc || '',
        marca: flags.marca,
        modelo: flags.modelo,
        modelosExtra: flags.modelosExtra,
        isUpdate: flags.isUpdate,
        forceUpload: flags.forceUpload,
      });
      if (result.ok) {
        setConflict(null);
        setNomeDoc('');
        setUploadsOpen(true);
        if (flags.draftKey) {
          setDrafts((prev) => prev.filter((draft) => draft.key !== flags.draftKey));
        }
        if (result.data?.jobId) {
          setPinnedJobs((prev) => [
            result.data,
            ...prev.filter((item) => item.jobId !== result.data.jobId),
          ]);
        }
        await load({ silent: true });
        return true;
      }
      if (result.status === 409 && result.conflict) {
        setConflict({
          file,
          message: result.message,
          draftKey: flags.draftKey || '',
          marca: flags.marca,
          modelo: flags.modelo,
          modelosExtra: flags.modelosExtra || '',
          nomeDoc: flags.nomeDoc || '',
          ...result.conflict,
        });
        setUploadsOpen(true);
        return false;
      }
      setConflict(null);
      setError(result.message || 'Falha ao enviar o manual');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onPickFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setUploadsOpen(true);
    setDrafts((prev) => prev.concat(files.map((file) => ({
      key: `${file.name}\u0001${file.size}\u0001${file.lastModified}\u0001${Math.random().toString(16).slice(2)}`,
      file,
      marca: '',
      links: [],
    }))));
  };

  const sendDrafts = async () => {
    const queue = drafts.filter((draft) => draftCodes(draft).length);
    for (const draft of queue) {
      const codes = draftCodes(draft);
      const sent = await sendFile(draft.file, {
        draftKey: draft.key,
        marca: draft.marca,
        modelo: codes[0],
        modelosExtra: codes.slice(1).join(', '),
        nomeDoc: queue.length === 1 ? nomeDoc : '',
      });
      if (!sent) break;
    }
  };

  const onRetry = async (job) => {
    setBusy(true);
    setError('');
    try {
      await retryKnowledgeJob(job.jobId, { forceUpload: false });
      await load({ silent: true });
    } catch (e) {
      setError(e.message || 'Falha ao tentar de novo');
    } finally {
      setBusy(false);
    }
  };

  const onRemoveJob = async (job) => {
    if (job.status === 'processando' || job.status === 'concluido') return;
    setBusy(true);
    setError('');
    try {
      await discardKnowledgeJob(job.jobId);
      setPinnedJobs((prev) => prev.filter((item) => item.jobId !== job.jobId));
      await load({ silent: true });
    } catch (e) {
      setError(e.message || 'Falha ao descartar');
    } finally {
      setBusy(false);
    }
  };

  const onDeleteDoc = async (doc) => {
    const label = doc.nomeDoc || doc.filename || 'este manual';
    if (!window.confirm(`Excluir ${label}? A Orya deixa de usar este manual.`)) return;
    setBusy(true);
    setError('');
    try {
      await deleteKnowledgeDocument(doc.docId);
      await load({ silent: true });
    } catch (e) {
      setError(e.message || 'Falha ao excluir');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const nProc = jobs.filter((job) => job.status === 'processando').length;
  const nQueue = jobs.filter((job) => job.status === 'na_fila').length;
  const nErr = jobs.filter((job) => job.status === 'falhou').length;
  const summary = [
    nProc ? `${nProc} processando` : '',
    nQueue ? `${nQueue} aguardando` : '',
    nErr ? `${nErr} com erro` : '',
  ].filter(Boolean).join(' · ');
  const activeLabel = [
    nProc ? `Processando ${nProc}` : '',
    nQueue ? `${nQueue} na fila` : '',
  ].filter(Boolean).join(' · ');

  const tabs = [
    ['all', 'Todos', rows.length],
    ['com', 'Com manual', withCount],
    ['sem', 'Sem manual', withoutCount],
  ];

  return (
    <div
      className="board-encaminhar-backdrop kit-create-kit-backdrop kit-create-kit-backdrop--products vp-kb-backdrop"
      onClick={() => !busy && onClose?.()}
    >
      <div
        className="vp-kb-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vp-kb-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="vp-kb-head">
          <span className="vp-kb-mark" aria-hidden><IconBook /></span>
          <h2 id="vp-kb-title">Base de conhecimento</h2>
          <div className="vp-kb-stats">
            <span>{rows.length.toLocaleString('pt-BR')} modelos locados</span>
            <i />
            <span className="vp-kb-cover" aria-hidden>
              <b style={{ width: `${Math.max(coverage, coverage ? 2 : 0)}%` }} />
            </span>
            <span>Cobertura {coverage.toLocaleString('pt-BR')}%</span>
          </div>
          <span className="vp-kb-spacer" />
          {nProc + nQueue > 0 ? (
            <button type="button" className="vp-kb-live" onClick={() => setUploadsOpen((v) => !v)}>
              <svg className="vp-kb-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.2-8.56" /></svg>
              {activeLabel}
            </button>
          ) : null}
          <button type="button" className="vp-kb-icon-btn" aria-label="Fechar" disabled={busy} onClick={() => onClose?.()}>
            <IconClose />
          </button>
        </header>

        <div className="vp-kb-bar">
          <label className="vp-kb-search">
            <IconSearch />
            <span className="vp-kb-sr">Buscar</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar marca ou equipamento"
            />
            <kbd>/</kbd>
          </label>
          <div className="vp-kb-tabs" role="tablist" aria-label="Filtrar por manual">
            {tabs.map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={tab === id ? 'is-on' : ''}
                onClick={() => setTab(id)}
              >
                <span>{label}</span>
                <em>{count.toLocaleString('pt-BR')}</em>
              </button>
            ))}
          </div>
          <button
            type="button"
            className={'vp-kb-send' + (uploadsOpen ? ' is-open' : '')}
            onClick={() => setUploadsOpen((v) => !v)}
            disabled={!configured}
          >
            <IconUpload />
            Enviar manuais
          </button>
        </div>

        {error ? <p className="vp-kb-error">{error}</p> : null}
        {conflict ? (
          <div className="vp-kb-conflict">
            <p>{conflict.message}</p>
            <div>
              <button type="button" disabled={busy} onClick={() => sendFile(conflict.file, { isUpdate: true, marca: conflict.marca, modelo: conflict.modelo, modelosExtra: conflict.modelosExtra, nomeDoc: conflict.nomeDoc, draftKey: conflict.draftKey })}>Substituir</button>
              {conflict.type !== 'exact_duplicate' && conflict.type !== 'ja_na_fila' ? (
                <button type="button" disabled={busy} onClick={() => sendFile(conflict.file, { forceUpload: true, marca: conflict.marca, modelo: conflict.modelo, modelosExtra: conflict.modelosExtra, nomeDoc: conflict.nomeDoc, draftKey: conflict.draftKey })}>Enviar mesmo assim</button>
              ) : null}
              <button type="button" disabled={busy} onClick={() => setConflict(null)}>Fechar</button>
            </div>
          </div>
        ) : null}

        {uploadsOpen ? (
          <section className={'vp-kb-drop' + (jobs.length || drafts.length ? '' : ' is-solo') + (drafts.length ? ' has-drafts' : '')} aria-label="Envio de manuais">
            <label className={'vp-kb-drop-zone' + (busy || !sectorId ? ' is-busy' : '')}>
              <span className="vp-kb-drop-ico"><IconUpload /></span>
              <span className="vp-kb-drop-title">Arraste os arquivos ou <em>escolha arquivos</em></span>
              <span className="vp-kb-drop-hint">Cada arquivo pode ter um ou mais modelos</span>
              <input type="file" multiple accept={ACCEPT} disabled={busy || !sectorId} onChange={onPickFiles} />
            </label>
            {jobs.length === 0 && drafts.length === 0 ? null : (
            <div className="vp-kb-queue">
              <div className="vp-kb-queue-head">
                <span>{drafts.length && !jobs.length ? 'Arquivos' : 'Fila'}</span>
                <em>{[drafts.length ? `${drafts.filter((draft) => draftCodes(draft).length).length} de ${drafts.length} vinculados` : '', summary].filter(Boolean).join(' · ') || 'Nenhum envio na fila'}</em>
                <span className="vp-kb-spacer" />
                {drafts.length ? (
                  <button
                    type="button"
                    className="vp-kb-send"
                    disabled={busy || !drafts.length || drafts.some((draft) => !draftCodes(draft).length)}
                    onClick={sendDrafts}
                  >
                    <IconUpload />
                    Enviar para a fila
                  </button>
                ) : null}
                <button type="button" className="vp-kb-icon-btn" aria-label="Recolher painel" onClick={() => setUploadsOpen(false)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg>
                </button>
              </div>
              {drafts.length === 1 ? (
                <label className="vp-kb-name">
                  <span>Nome do documento</span>
                  <input value={nomeDoc} onChange={(e) => setNomeDoc(e.target.value)} placeholder="Padrão: nome do arquivo" disabled={busy} />
                </label>
              ) : null}
              {sectors.length > 1 ? (
                <label className="vp-kb-name">
                  <span>Setor</span>
                  <select value={sectorId} onChange={(e) => setSectorId(e.target.value)} disabled={busy}>
                    <option value="">Selecione</option>
                    {sectors.map((sector) => <option key={sector.id} value={sector.id}>{sector.name}</option>)}
                  </select>
                </label>
              ) : null}
              {payload && configured && sectors.length === 0 && !loading ? (
                <p className="vp-kb-muted">Nenhum setor da Orya disponível para este envio.</p>
              ) : null}
              {drafts.length ? (
                <div className="vp-kb-drafts">
                  {drafts.map((draft) => {
                    const links = draft.links || [];
                    const codes = draftCodes(draft);
                    const selectedLabels = links.map((link) => choiceByKey.get(link.catalogKey)?.label).filter(Boolean);
                    const only = links.length === 1 ? choiceByKey.get(links[0].catalogKey) : null;
                    const note = codes.length ? codes.join(', ') : 'Escolha um ou mais modelos';
                    return (
                      <div key={draft.key} className="vp-kb-draft">
                        <div className="vp-kb-draft-main">
                          <strong className="vp-kb-draft-name">{draft.file.name}</strong>
                          <div className="vp-kb-draft-fields">
                            <BoardClickUpFilterChipDropdown
                              label="Modelo"
                              icon={IconPrinter}
                              options={modelChoices.map((item) => item.label)}
                              selected={selectedLabels}
                              selectionDisplay={only ? only.modelo : (links.length > 1 ? `${links.length} modelos` : '')}
                              isOpen={openModelKey === draft.key}
                              onOpenChange={(open) => setOpenModelKey(open ? draft.key : '')}
                              onToggleItem={(label) => {
                                const next = choiceByLabel.get(label);
                                if (next) toggleLink(draft.key, next.key);
                              }}
                              disabled={busy}
                              pickerMode="simple"
                              pickerWidth={520}
                              pickerMaxHeight={360}
                              searchPlaceholder="Buscar equipamento"
                              countLabel="equipamentos"
                              listAriaLabel={`Modelo de ${draft.file.name}`}
                              popoverClassName="ce-compat-eq-popover"
                              emptyMessage="Nenhum equipamento encontrado"
                              showSearch={modelChoices.length > 8}
                              getOptionGroup={(label) => choiceByLabel.get(label)?.marca || ''}
                              showOptionGroupCount={false}
                            />
                            <button
                              type="button"
                              className="vp-kb-icon-btn"
                              aria-label={`Remover ${draft.file.name}`}
                              disabled={busy}
                              onClick={() => {
                                setDrafts((prev) => prev.filter((row) => row.key !== draft.key));
                                setOpenModelKey((current) => (current === draft.key ? '' : current));
                              }}
                            >
                              <IconClose size={12} />
                            </button>
                          </div>
                          <span className="vp-kb-draft-note">{note}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="vp-kb-queue-list">
                {jobs.map((job) => {
                  const queuePos = jobs.filter((item) => item.status === 'na_fila').indexOf(job) + 1;
                  const size = formatBytes(job.fileSize);
                  const statusText = job.status === 'na_fila'
                    ? `Na fila · ${job.posicao || queuePos}º`
                    : job.status === 'falhou'
                      ? 'Falhou'
                      : job.status === 'concluido'
                        ? ([job.marca, job.modelo].filter(Boolean).join(' · ') || 'Concluído')
                        : (job.progresso != null ? `${job.progresso}%` : 'Processando');
                  return (
                    <div key={job.jobId} className="vp-kb-q">
                      <span className={'vp-kb-q-ico is-' + (job.status || 'na_fila')} aria-hidden>
                        {job.status === 'processando' ? (
                          <svg className="vp-kb-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.2-8.56" /></svg>
                        ) : job.status === 'falhou' ? '!' : job.status === 'concluido' ? '✓' : '◦'}
                      </span>
                      <div className="vp-kb-q-name">
                        <strong>{job.filename || nomeDoc || `Envio ${job.jobId}`}</strong>
                        <span>{[size, job.erro].filter(Boolean).join(' · ')}</span>
                      </div>
                      <div className="vp-kb-q-status">
                        {job.status === 'processando' ? (
                          <>
                            <span className="vp-kb-meter"><b style={{ width: job.progresso != null ? `${job.progresso}%` : undefined }} className={job.progresso == null ? 'is-run' : ''} /></span>
                            <em>{job.progresso != null ? `${job.progresso}%` : ''}</em>
                          </>
                        ) : (
                          <span className={'vp-kb-pill is-' + job.status}>{statusText}</span>
                        )}
                      </div>
                      <div className="vp-kb-q-actions">
                        {job.status === 'falhou' ? (
                          <button type="button" className="vp-kb-icon-btn" aria-label="Tentar novamente" disabled={busy} onClick={() => onRetry(job)}>↻</button>
                        ) : null}
                        {job.status !== 'processando' ? (
                          <button type="button" className="vp-kb-icon-btn" aria-label="Remover da fila" disabled={busy} onClick={() => onRemoveJob(job)}>
                            <IconClose size={12} />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </section>
        ) : null}

        <div className="vp-kb-sheet">
          <div className="vp-kb-cols">
            <span>
              Marca
              <button
                ref={brandFilterRef}
                type="button"
                className="filter-icon-button"
                aria-label="Filtrar Marca"
                aria-expanded={brandOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  setBrandOpen((v) => !v);
                }}
              >
                <Funnel size={14} />
                {brands.length ? <span className="perm-filter-badge-dot" aria-hidden /> : null}
              </button>
            </span>
            <span>Equipamento</span>
            <span className="vp-kb-cols-num">Locadas</span>
            <span>Manual</span>
          </div>
          <PermColumnFilterPopover
            anchorRef={brandFilterRef}
            isOpen={brandOpen}
            onClose={() => setBrandOpen(false)}
            options={brandOptions}
            selected={brands}
            onApply={setBrands}
            listAriaLabel="Filtrar por marca"
            searchPlaceholder="Buscar marca"
            showSearch={brandOptions.length > 8}
          />
          <div className="vp-kb-rows">
            {visibleRows.map((row) => (
              <div className="vp-kb-row" key={`${row.marca}-${row.modelo}`}>
                <span className="vp-kb-brand-cell">{row.marca}</span>
                <span className="vp-kb-name-cell">{row.modelo}</span>
                <span className="vp-kb-qty">
                  <i><b style={{ width: `${Math.max(3, Math.round((row.qtde / maxQty) * 100))}%` }} className={row.manuais > 0 ? '' : 'is-empty'} /></i>
                  <em>{row.qtde.toLocaleString('pt-BR')}</em>
                </span>
                <span className="vp-kb-manual-cell">
                  {row.manuais > 0 ? (
                    <button type="button" className="vp-kb-chip" aria-label="Ver manuais vinculados" onClick={() => { setSelected(row); setBrandOpen(false); }}>
                      <IconDoc />
                      {manualLabel(row.manuais)}
                    </button>
                  ) : (
                    <>
                      <span className="vp-kb-none">Sem manual</span>
                      <button type="button" className="vp-kb-row-send" onClick={() => setUploadsOpen(true)}>Enviar</button>
                    </>
                  )}
                </span>
              </div>
            ))}
            {!loading && visibleRows.length === 0 ? (
              <p className="vp-kb-empty">Nenhum equipamento encontrado com os filtros atuais.</p>
            ) : null}
          </div>
        </div>

        <footer className="vp-kb-foot">
          <span>
            Exibindo {visibleRows.length.toLocaleString('pt-BR')} de {(
              tab === 'com' ? withCount : tab === 'sem' ? withoutCount : rows.length
            ).toLocaleString('pt-BR')} modelos
            {brands.length ? ` · marcas: ${brands.join(', ')}` : ''}
          </span>
          <span className="vp-kb-spacer" />
          <span>Ordenado por quantidade locada</span>
        </footer>

        {selected ? (
          <div className="vp-kb-docs-back" onClick={() => setSelected(null)}>
            <div className="vp-kb-docs" role="dialog" aria-label="Manuais vinculados" onClick={(e) => e.stopPropagation()}>
              <div className="vp-kb-docs-head">
                <div>
                  <h3>Manuais vinculados <em>· {linkedDocs.length || selected.manuais}</em></h3>
                  <span>{selected.marca} · {selected.modelo}</span>
                </div>
                <button type="button" className="vp-kb-icon-btn" aria-label="Fechar" onClick={() => setSelected(null)}>
                  <IconClose size={14} />
                </button>
              </div>
              <div className="vp-kb-docs-list">
                {linkedDocs.length === 0 ? <p className="vp-kb-muted">Nenhum documento deste modelo.</p> : null}
                {linkedDocs.map((doc) => (
                  <div className="vp-kb-doc" key={doc.docId || doc.filename}>
                    <span className="vp-kb-ext">{String(doc.filename || 'PDF').split('.').pop().slice(0, 4).toUpperCase()}</span>
                    <div>
                      <strong>{doc.nomeDoc || doc.filename}</strong>
                      <span>{[doc.marca, doc.modelo].filter(Boolean).join(' · ')}</span>
                    </div>
                    <span className="vp-kb-indexed">Indexado</span>
                    <button type="button" className="vp-kb-icon-btn" aria-label="Remover vínculo" disabled={busy} onClick={() => onDeleteDoc(doc)}>
                      <IconTrash size={13} stroke={1.8} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="vp-kb-docs-foot">
                <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={() => setSelected(null)}>Fechar</button>
                <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={() => { setSelected(null); setUploadsOpen(true); }}>
                  Adicionar manual
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
